import { cn } from '@/shared/lib/utils'
import { motion } from 'framer-motion'
import { ReactNode } from 'react'

interface PremiumCardProps {
    /** Main title displayed at the bottom of the card */
    title: string
    /** Optional subtitle/description shown on hover */
    subtitle?: string
    /** Background image URL */
    image?: string
    /** Optional badge content for the top-left pill */
    badge?: ReactNode
    /** Click handler (used when `href` is not provided) */
    onClick?: () => void
    /** Navigation link. When provided, renders an <a> tag instead of a button */
    href?: string
    /** Additional class names for the outer wrapper */
    className?: string
    /** Aspect ratio of the card. Defaults to '3/4' */
    aspectRatio?: '3/4' | '16/9' | 'square'
    /** Disables interaction and dims the card */
    isEnabled?: boolean
}

export const PremiumCard = ({
    title,
    subtitle,
    image,
    badge,
    onClick,
    href,
    className,
    aspectRatio = '3/4',
    isEnabled = true,
}: PremiumCardProps) => {
    const inner = (
        <div
            className={cn(
                'relative w-full overflow-hidden rounded-[24px] border border-white/10 bg-[#0f0f11] shadow-xl',
                'transition-all duration-500 hover:shadow-2xl hover:border-primary/40 cursor-pointer',
                aspectRatio === '3/4' && 'aspect-[3/4]',
                aspectRatio === '16/9' && 'aspect-[16/9]',
                aspectRatio === 'square' && 'aspect-square',
                !isEnabled && 'opacity-50 pointer-events-none',
                className
            )}
        >
            {/* Badge (Top Left Corner) */}
            {badge && (
                <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md px-3 py-1.5 border border-white/20 shadow-sm transition-transform duration-500 group-hover:scale-105">
                    {typeof badge === 'string' ? (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-white/90">
                            {badge}
                        </span>
                    ) : (
                        badge
                    )}
                </div>
            )}

            {/* Image Background */}
            {image && (
                <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-[1.03] opacity-80 mix-blend-screen"
                    style={{ backgroundImage: `url(${image})` }}
                />
            )}

            {/* Gradients for Mood & Vignette */}
            <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors duration-500" />
            <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

            {/* Inner Glow Border */}
            <div className="absolute inset-0 rounded-[24px] ring-1 ring-inset ring-white/10 transition-all duration-500 group-hover:ring-white/20 pointer-events-none" />

            {/* Content */}
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex flex-col justify-end">
                <div className="transform transition-transform duration-500 ease-out group-hover:-translate-y-2">
                    <h3 className="text-3xl font-extrabold tracking-tight text-white drop-shadow-xl mb-2 line-clamp-3 leading-snug">
                        {title}
                    </h3>
                    {subtitle && (
                        <p className="text-white/90 text-[15px] leading-relaxed line-clamp-2 h-0 opacity-0 overflow-hidden transition-all duration-500 ease-out group-hover:h-auto group-hover:opacity-100 group-hover:mt-2 drop-shadow-md">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        </div>
    )

    if (href) {
        return (
            <motion.a
                href={href}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="block group w-full outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-[24px]"
            >
                {inner}
            </motion.a>
        )
    }

    return (
        <motion.button
            type="button"
            onClick={onClick}
            whileHover={{ y: -5 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="block group w-full text-left outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-[24px]"
        >
            {inner}
        </motion.button>
    )
}
